import axios, { AxiosError } from 'axios';
import { auth } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { backendAPI } from './products';
import type { ProductMedia } from '../types/commerce';

interface ApiError {
  message?: string;
  error?: string;
}

export type MediaCreatePayload = {
  url?: string;
  altText?: string | null;
  thumbnailUrl?: string | null;
  type?: string;
  isPrimary?: boolean;
  displayOrder?: number;
  fileBase64?: string;
  fileName?: string;
};

export type MediaUpdatePayload = {
  altText?: string | null;
  thumbnailUrl?: string | null;
  metadata?: Record<string, unknown> | null;
};

async function authHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) throw new Error('User not logged in');
  const idToken = await user.getIdToken();
  return { Authorization: `Bearer ${idToken}` };
}

function mapMediaError(error: unknown, fallback: string): Error {
  const axiosError = error as AxiosError<ApiError>;
  const message =
    axiosError?.response?.data?.error ||
    axiosError?.response?.data?.message ||
    fallback;
  return new Error(message);
}

export async function uploadProductImageFile(
  businessId: number,
  productId: number,
  file: File
): Promise<string> {
  const fileRef = ref(
    storage,
    `products/${businessId}/${productId}/${Date.now()}-${file.name}`
  );
  await uploadBytes(fileRef, file);
  return getDownloadURL(fileRef);
}

export const list_product_media = async (productId: number): Promise<ProductMedia[]> => {
  try {
    const response = await backendAPI.get(`/products/${productId}/media`);
    return response.data?.media ?? [];
  } catch (error) {
    throw mapMediaError(error, 'Failed to fetch product media');
  }
};

export const create_product_media = async (
  productId: number,
  payload: MediaCreatePayload
): Promise<ProductMedia> => {
  try {
    const headers = await authHeaders();
    const response = await backendAPI.post(
      `/products/${productId}/media`,
      payload,
      { headers }
    );
    return response.data?.media;
  } catch (error) {
    throw mapMediaError(error, 'Failed to upload media');
  }
};

export const update_product_media = async (
  mediaId: number,
  payload: MediaUpdatePayload
): Promise<ProductMedia> => {
  try {
    const headers = await authHeaders();
    const response = await backendAPI.patch(
      `/products/media/${mediaId}`,
      payload,
      { headers }
    );
    return response.data?.media;
  } catch (error) {
    throw mapMediaError(error, 'Failed to update media');
  }
};

export const delete_product_media = async (mediaId: number): Promise<void> => {
  try {
    const headers = await authHeaders();
    await backendAPI.delete(`/products/media/${mediaId}`, { headers });
  } catch (error) {
    throw mapMediaError(error, 'Failed to delete media');
  }
};

export const set_primary_product_media = async (
  mediaId: number
): Promise<ProductMedia> => {
  try {
    const headers = await authHeaders();
    const response = await backendAPI.post(
      `/products/media/${mediaId}/set-primary`,
      {},
      { headers }
    );
    return response.data?.media;
  } catch (error) {
    throw mapMediaError(error, 'Failed to set primary media');
  }
};

export const reorder_product_media = async (
  productId: number,
  orderedIds: number[]
): Promise<ProductMedia[]> => {
  try {
    const headers = await authHeaders();
    const response = await backendAPI.post(
      `/products/${productId}/media/reorder`,
      { orderedIds },
      { headers }
    );
    return response.data?.media ?? [];
  } catch (error) {
    throw mapMediaError(error, 'Failed to reorder media');
  }
};
